import React, { useEffect, useMemo, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useBranchFreeze } from "@/lib/useBranchFreeze";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Printer, Calendar as CalendarIcon, AlertCircle, FileBarChart2,
} from "lucide-react";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtCoverage = (df, dt) => {
  if (!df && !dt) return "—";
  const f = (s) => {
    const d = new Date(s + "T00:00:00");
    return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };
  if (df === dt) return f(df);
  return `${f(df)} — ${f(dt)}`;
};

export default function AccountingReportsPage() {
  const { user } = useAuth();
  const { allBranches, selectedSchool } = useBranch();
  const { frozenBranch, lockedBranchId } = useBranchFreeze();
  const currency = user?.currency_symbol || "₱";

  const branchesForSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchool?.id && b.status === "approved"),
    [allBranches, selectedSchool?.id]
  );
  const [branchFilter, setBranchFilter] = useState(() => lockedBranchId || "all");
  const effBranchFilter = frozenBranch && lockedBranchId ? lockedBranchId : branchFilter;

  if (!selectedSchool) {
    return (
      <>
        <PageHeader />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="rep-no-school">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Accounting Reports is scoped per school.</p>
        </div>
      </>
    );
  }

  const baseParams = {
    school_id: selectedSchool.id,
    ...(effBranchFilter !== "all" ? { branch_id: effBranchFilter } : {}),
  };

  return (
    <>
      <PageHeader />

      {/* Global filter */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 mb-5" data-testid="rep-global-filter">
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldBlock label="School">
            <Input value={selectedSchool.name} disabled className="rounded-xl h-10 bg-brand-purpleLight/30" />
          </FieldBlock>
          <FieldBlock label="Branch">
            <select
              value={effBranchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              disabled={frozenBranch}
              title={frozenBranch ? "Locked to your assigned branch" : undefined}
              className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple disabled:bg-brand-purpleLight/30 disabled:cursor-not-allowed disabled:opacity-80"
              data-testid="rep-filter-branch"
            >
              {!frozenBranch && <option value="all">All Branches</option>}
              {branchesForSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </FieldBlock>
        </div>
      </section>

      <ReportCard
        title="School Year to Present Report"
        description="Summary from the beginning of the current school year up to today."
        baseParams={{ ...baseParams, period_type: "sy_to_date" }}
        autoRun
        currency={currency}
        testidPrefix="rep-sy"
      />

      <ReportCard
        title="Monthly Report"
        description="Summary from the 1st day to the last day of the selected month."
        baseParams={baseParams}
        currency={currency}
        testidPrefix="rep-monthly"
        autoRun
        controls={MonthlyControls}
        buildParams={({ year, month }) => ({ period_type: "monthly", year, month })}
        defaults={{ year: new Date().getFullYear(), month: new Date().getMonth() + 1 }}
      />

      <ReportCard
        title="Weekly Report"
        description="Summary from Monday to Sunday of the selected week."
        baseParams={baseParams}
        currency={currency}
        testidPrefix="rep-weekly"
        autoRun
        controls={WeeklyControls}
        buildParams={({ week_pick }) => ({ period_type: "weekly", week_pick })}
        defaults={{ week_pick: new Date().toISOString().slice(0, 10) }}
      />

      <ReportCard
        title="Daily Report"
        description="Summary for the selected single day."
        baseParams={baseParams}
        currency={currency}
        testidPrefix="rep-daily"
        autoRun
        controls={DailyControls}
        buildParams={({ day }) => ({ period_type: "daily", day })}
        defaults={{ day: new Date().toISOString().slice(0, 10) }}
      />

      <ReportCard
        title="Custom Date Range Report"
        description="Choose a beginning date and end date to generate a report."
        baseParams={baseParams}
        currency={currency}
        testidPrefix="rep-custom"
        controls={CustomControls}
        buildParams={({ date_from, date_to }) => ({ period_type: "custom", date_from, date_to })}
        defaults={{ date_from: "", date_to: "" }}
        manualTrigger
      />
    </>
  );
}

function PageHeader() {
  return (
    <div className="mb-5">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight flex items-center gap-2">
        <FileBarChart2 className="w-7 h-7 text-brand-purple" /> Reports
      </h1>
      <p className="text-brand-inkMuted mt-0.5 text-sm">
        Income, expenses and net balance summaries — by school year, month, week, day, or custom range.
      </p>
    </div>
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <Label className="font-heading text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReportCard({
  title, description, baseParams, controls, buildParams, defaults, autoRun, manualTrigger,
  currency, testidPrefix,
}) {
  const [controlState, setControlState] = useState(defaults || {});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const merged = { ...baseParams, ...(buildParams ? buildParams(controlState) : {}) };
      // Don't fire if required values not filled
      if (merged.period_type === "custom" && (!merged.date_from || !merged.date_to)) {
        setData(null); setLoading(false); return;
      }
      const { data: res } = await api.get("/accounting/reports", { params: merged });
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [baseParams, buildParams, controlState]);

  useEffect(() => {
    if (autoRun) run();
  }, [run, autoRun]);

  const print = () => {
    if (typeof window === "undefined") return;
    const el = document.querySelector(`[data-print-id='${testidPrefix}']`);
    if (!el) return;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) return;
    // Build the print document via DOM APIs — never via document.write of
    // template-literal strings. Title + report HTML are sanitised through
    // DOMPurify so any unexpected script/event handlers from user-entered
    // category names can't execute in the print window.
    w.document.open();
    w.document.write("<!DOCTYPE html><html><head></head><body></body></html>");
    w.document.close();
    const doc = w.document;
    doc.title = String(title || "Report");
    const style = doc.createElement("style");
    style.textContent = `
      body{font-family:system-ui,sans-serif;padding:24px;color:#111}
      h2{margin:0 0 6px;font-size:22px}
      .desc{color:#666;font-size:13px;margin-bottom:14px}
      .coverage{color:#444;font-size:12px;margin-bottom:18px}
      .section-label{font-size:11px;letter-spacing:.1em;color:#777;text-transform:uppercase;margin:14px 0 8px}
      .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee}
      .row.sub{font-weight:700;border-top:1px solid #444}
      .row.total{font-weight:800;border-top:2px solid #111;border-bottom:none;font-size:16px;padding-top:10px}
      .indent{padding-left:20px;color:#444}
    `;
    doc.head.appendChild(style);
    doc.body.innerHTML = DOMPurify.sanitize(el.innerHTML, { USE_PROFILES: { html: true } });
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  return (
    <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-5 mb-5" data-testid={testidPrefix}>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: report body */}
        <div data-print-id={testidPrefix}>
          <h2 className="font-heading text-2xl font-semibold text-foreground">{title}</h2>
          <p className="desc text-brand-inkMuted text-sm mt-0.5 mb-3">{description}</p>
          <p className="coverage text-xs text-brand-inkMuted mb-4">
            Coverage: <span className="font-semibold text-foreground">{data ? fmtCoverage(data.date_from, data.date_to) : "—"}</span>
            {data?.school_year_label && <span className="ml-2 text-brand-purple">· SY {data.school_year_label}</span>}
          </p>

          {controls && React.createElement(controls, { state: controlState, setState: setControlState, onRun: manualTrigger ? run : undefined, testidPrefix })}

          <ReportBody data={data} currency={currency} loading={loading} testidPrefix={testidPrefix} />

          <Button
            type="button"
            onClick={print}
            disabled={!data || loading}
            variant="outline"
            className="w-full mt-4 rounded-2xl border-brand-purple/30 h-11 hover:bg-brand-purpleLight"
            data-testid={`${testidPrefix}-print-btn`}
          >
            <Printer className="w-4 h-4 mr-2" /> Print Report
          </Button>
        </div>

        {/* Right: chart */}
        <div>
          <h3 className="font-heading text-xl font-semibold text-foreground">Income vs Expenses Overview</h3>
          <p className="text-brand-inkMuted text-sm mt-0.5 mb-4">Compare Total Income, Total Expenses and Net Balance.</p>
          <ChartPanel data={data} currency={currency} testidPrefix={testidPrefix} />
        </div>
      </div>
    </section>
  );
}

function ReportBody({ data, currency, loading, testidPrefix }) {
  const peso = (n) => `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (loading) return <div className="py-10 text-center text-brand-inkMuted">Loading...</div>;
  if (!data) {
    return (
      <div className="rounded-xl bg-[#FAFBFE] border border-brand-purple/10 p-6 text-center text-brand-inkMuted text-sm">
        Pick a date range above to generate the report.
      </div>
    );
  }
  const i = data.income;
  const e = data.expenses;
  return (
    <div className="space-y-1.5 text-sm">
      <SectionLabel>Income vs Expenses Summary</SectionLabel>
      <Row label="Total Income" value={peso(data.totals.total_income)} testid={`${testidPrefix}-total-income`} />
      <Row label="Total Expenses" value={peso(data.totals.total_expenses)} testid={`${testidPrefix}-total-expenses`} />
      <Row label="Net Balance" value={peso(data.totals.net_balance)} bold separator testid={`${testidPrefix}-net-balance`} />

      <SectionLabel>Income Breakdown</SectionLabel>
      <Row label="Initial Payments" value={peso(i.initial_payments)} indent />
      <Row label="Tuition" value={peso(i.tuition)} indent />
      <Row label="Uniforms" value={peso(i.uniforms)} indent />
      <Row label="Books" value={peso(i.books)} indent />
      <Row label="Events" value={peso(i.events)} indent />
      <Row label="Individual Sessions" value={peso(i.individual_sessions)} indent />
      <Row label="Others" value={peso(i.others)} indent />
      <Row label="Student Payments" value={peso(i.student_payments)} bold separator />
      <Row label="Manual Income" value={peso(i.manual_income)} bold />
      <Row label="Grand Total Income" value={peso(i.grand_total_income)} bold double />

      <SectionLabel>Expenses Breakdown</SectionLabel>
      <Row label="Salaries" value={peso(e.salaries)} indent />
      <Row label="Employee Advances" value={peso(e.employee_advances)} indent />
      <Row label="Manual Expenses" value={peso(e.manual_expenses)} indent />
      <Row label="Total Expenses" value={peso(e.total_expenses)} bold double />
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="section-label text-[10px] font-heading uppercase tracking-[0.15em] text-brand-inkMuted mt-5 mb-2">{children}</p>;
}

function Row({ label, value, indent, bold, double, separator, testid }) {
  const cls = [
    "row flex items-center justify-between py-1.5",
    indent ? "indent pl-5 text-foreground" : "",
    bold ? "font-semibold text-foreground" : "",
    double ? "border-t-2 border-foreground/70 pt-2 mt-1 text-[15px]" : separator ? "border-t border-brand-purple/15 pt-2 mt-1" : "",
  ].join(" ");
  return (
    <div className={cls} data-testid={testid}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ChartPanel({ data, currency, testidPrefix }) {
  const peso = (n) => `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const t = data?.totals || { total_income: 0, total_expenses: 0, net_balance: 0 };
  const bars = [
    { label: "Income", value: t.total_income, color: "#2E7D32" },
    { label: "Expenses", value: t.total_expenses, color: "#C62828" },
    { label: "Net Balance", value: Math.max(t.net_balance, 0), color: "#1565C0", actual: t.net_balance },
  ];
  const max = Math.max(...bars.map((b) => Math.abs(b.value)), 1);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-chart`}>
      <div className="grid grid-cols-3 gap-2">
        <StatBox label="Income" value={peso(t.total_income)} color="green" testid={`${testidPrefix}-stat-income`} />
        <StatBox label="Expenses" value={peso(t.total_expenses)} color="red" testid={`${testidPrefix}-stat-expenses`} />
        <StatBox label="Net Balance" value={peso(t.net_balance)} color="blue" testid={`${testidPrefix}-stat-net`} />
      </div>
      <div className="bg-[#FAFBFE] border border-brand-purple/10 rounded-2xl p-4 h-56 flex items-end justify-around gap-4">
        {bars.map((b) => {
          const h = Math.max((Math.abs(b.value) / max) * 100, b.value !== 0 ? 6 : 2);
          return (
            <div key={b.label} className="flex-1 h-full flex flex-col items-center justify-end gap-2 max-w-[100px]">
              <span className="text-[11px] text-foreground font-semibold">{peso(b.actual ?? b.value)}</span>
              <div className="w-full rounded-t-lg transition-all" style={{ height: `${h}%`, background: b.color, minHeight: "4px" }} />
              <span className="text-xs text-brand-inkMuted font-heading">{b.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatBox({ label, value, color, testid }) {
  const styles = {
    green: "bg-[#E8F5E9] border-[#A5D6A7]/40 text-[#2E7D32]",
    red: "bg-[#FFEBEE] border-[#EF9A9A]/40 text-[#C62828]",
    blue: "bg-[#E3F2FD] border-[#90CAF9]/40 text-[#1565C0]",
  }[color];
  return (
    <div className={`rounded-2xl border px-3 py-2 ${styles}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-heading opacity-80">{label}</p>
      <p className="text-sm font-bold">{value}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-card controls
// -----------------------------------------------------------------------------
function MonthlyControls({ state, setState, testidPrefix }) {
  const years = [];
  const cy = new Date().getFullYear();
  for (let y = cy + 1; y >= cy - 5; y--) years.push(y);
  return (
    <div className="grid grid-cols-2 gap-3 mb-3">
      <FieldBlock label="Month">
        <select value={state.month} onChange={(e) => setState({ ...state, month: Number(e.target.value) })}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
                data-testid={`${testidPrefix}-month`}>
          {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
        </select>
      </FieldBlock>
      <FieldBlock label="Year">
        <select value={state.year} onChange={(e) => setState({ ...state, year: Number(e.target.value) })}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
                data-testid={`${testidPrefix}-year`}>
          {years.map((y) => (<option key={y} value={y}>{y}</option>))}
        </select>
      </FieldBlock>
    </div>
  );
}

function WeeklyControls({ state, setState, testidPrefix }) {
  return (
    <div className="mb-3">
      <FieldBlock label="Pick any date in the week">
        <Input type="date" value={state.week_pick} onChange={(e) => setState({ ...state, week_pick: e.target.value })}
               className="rounded-2xl h-11 border-brand-purple/20" data-testid={`${testidPrefix}-week-pick`} />
      </FieldBlock>
    </div>
  );
}

function DailyControls({ state, setState, testidPrefix }) {
  return (
    <div className="mb-3">
      <FieldBlock label="Date">
        <Input type="date" value={state.day} onChange={(e) => setState({ ...state, day: e.target.value })}
               className="rounded-2xl h-11 border-brand-purple/20" data-testid={`${testidPrefix}-day`} />
      </FieldBlock>
    </div>
  );
}

function CustomControls({ state, setState, onRun, testidPrefix }) {
  return (
    <div className="mb-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <FieldBlock label="Beginning Date">
          <Input type="date" value={state.date_from} onChange={(e) => setState({ ...state, date_from: e.target.value })}
                 className="rounded-2xl h-11 border-brand-purple/20" data-testid={`${testidPrefix}-from`} />
        </FieldBlock>
        <FieldBlock label="End Date">
          <Input type="date" value={state.date_to} onChange={(e) => setState({ ...state, date_to: e.target.value })}
                 className="rounded-2xl h-11 border-brand-purple/20" data-testid={`${testidPrefix}-to`} />
        </FieldBlock>
      </div>
      <Button
        type="button"
        onClick={onRun}
        disabled={!state.date_from || !state.date_to}
        className="w-full rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11"
        data-testid={`${testidPrefix}-generate-btn`}
      >
        <CalendarIcon className="w-4 h-4 mr-2" /> Generate Report
      </Button>
    </div>
  );
}
